import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  AlertTriangle,
  CheckCircle,
  XCircle,
  Shield,
  Eye,
  Ban,
  Key,
  FileText,
  Settings,
  User,
} from 'lucide-react';

export default function AuditLogsPage() {
  const { api } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ action_type: '', target_entity: '' });

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        if (filters.action_type && filters.action_type !== 'all') params.append('action_type', filters.action_type);
        if (filters.target_entity && filters.target_entity !== 'all') params.append('target_entity', filters.target_entity);
        params.append('limit', '100');

        const response = await api.get(`/admin/audit-logs/?${params.toString()}`);
        setLogs(response.data);
      } catch (error) {
        toast.error('Failed to fetch audit logs');
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, [api, filters]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getActionIcon = (actionType) => {
    const icons = {
      merchant_created: FileText,
      merchant_approved: CheckCircle,
      merchant_rejected: XCircle,
      kyc_status_changed: Eye,
      risk_level_changed: Shield,
      mdr_changed: Settings,
      settlement_hold: Ban,
      settlement_released: CheckCircle,
      payout_frozen: Ban,
      payout_unfrozen: CheckCircle,
      api_key_generated: Key,
      reconciliation_uploaded: FileText,
    };
    return icons[actionType] || AlertTriangle;
  };

  const getActionBadge = (actionType) => {
    const colors = {
      merchant_created: 'bg-blue-500/20 text-blue-500 border-blue-500/30',
      merchant_approved: 'bg-green-500/20 text-green-500 border-green-500/30',
      merchant_rejected: 'bg-red-500/20 text-red-500 border-red-500/30',
      risk_level_changed: 'bg-orange-500/20 text-orange-500 border-orange-500/30',
      payout_frozen: 'bg-red-500/20 text-red-500 border-red-500/30',
      payout_unfrozen: 'bg-green-500/20 text-green-500 border-green-500/30',
      settlement_hold: 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30',
      settlement_released: 'bg-green-500/20 text-green-500 border-green-500/30',
    };
    const Icon = getActionIcon(actionType);
    return (
      <Badge variant="outline" className={`${colors[actionType] || 'bg-muted'} rounded-sm`}>
        <Icon className="h-3 w-3 mr-1" />
        {actionType?.replace(/_/g, ' ').toUpperCase()}
      </Badge>
    );
  };

  const ACTION_TYPES = [
    'merchant_created',
    'merchant_approved',
    'merchant_rejected',
    'kyc_status_changed',
    'risk_level_changed',
    'payout_frozen',
    'payout_unfrozen',
    'settlement_hold',
    'settlement_released',
    'api_key_generated',
    'reconciliation_uploaded',
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-heading font-bold">Audit Logs</h1>
        <p className="text-muted-foreground text-sm">Track all admin actions and system changes</p>
      </div>

      {/* Filters */}
      <Card className="card-grid">
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <Select
              value={filters.action_type}
              onValueChange={(value) => setFilters({ ...filters, action_type: value })}
            >
              <SelectTrigger className="w-[200px] rounded-sm" data-testid="action-type-filter">
                <SelectValue placeholder="All Actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                {ACTION_TYPES.map(type => (
                  <SelectItem key={type} value={type}>
                    {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filters.target_entity}
              onValueChange={(value) => setFilters({ ...filters, target_entity: value })}
            >
              <SelectTrigger className="w-[150px] rounded-sm" data-testid="entity-filter">
                <SelectValue placeholder="All Entities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Entities</SelectItem>
                <SelectItem value="merchant">Merchant</SelectItem>
                <SelectItem value="settlement">Settlement</SelectItem>
                <SelectItem value="reconciliation">Reconciliation</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card className="card-grid">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table className="table-high-density">
              <TableHeader>
                <TableRow className="border-b border-border/50 hover:bg-transparent">
                  <TableHead className="text-xs uppercase tracking-wider">Timestamp</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Action</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Performed By</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Role</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Target</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  [...Array(10)].map((_, i) => (
                    <TableRow key={i}>
                      {[...Array(6)].map((_, j) => (
                        <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : logs.length > 0 ? (
                  logs.map((log) => (
                    <TableRow key={log.id} className="border-b border-border/50" data-testid={`audit-row-${log.id}`}>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {formatDate(log.created_at)}
                      </TableCell>
                      <TableCell>{getActionBadge(log.action_type)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <span className="font-mono text-sm">{log.performed_by?.slice(0, 8)}...</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm capitalize">{log.performed_by_role?.replace('_', ' ')}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="text-xs uppercase text-muted-foreground">{log.target_entity}:</span>
                          <span className="font-mono text-sm">{log.target_id?.slice(0, 8)}...</span>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-xs">
                        <div className="text-sm text-muted-foreground truncate">
                          {log.details?.reason || log.details?.new_status || log.details?.risk_category || JSON.stringify(log.details).slice(0, 50)}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      <AlertTriangle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      No audit logs found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
